(() => {
  // -- Inputs and outputs
  const themeRuntime = window.theme;

  // -- Functions
  const exposeConceptRuntime = () => {
    if (!themeRuntime) return;

    window.routes = themeRuntime.routes;
    window.PUB_SUB_EVENTS = themeRuntime.pubsub?.PUB_SUB_EVENTS;
    window.subscribe = themeRuntime.pubsub?.subscribe?.bind(themeRuntime.pubsub);
    window.publish = themeRuntime.pubsub?.publish?.bind(themeRuntime.pubsub);
    window.trapFocus = themeRuntime.a11y?.trapFocus?.bind(themeRuntime.a11y);
    window.removeTrapFocus = themeRuntime.a11y?.removeTrapFocus?.bind(themeRuntime.a11y);
  };

  const captureStyles = (element, properties) =>
    properties.reduce((styles, property) => {
      styles[property] = {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property),
      };
      return styles;
    }, {});

  const restoreStyles = (element, styles, excludedProperties = []) => {
    Object.entries(styles).forEach(([property, style]) => {
      if (excludedProperties.includes(property)) return;

      if (style.value) {
        element.style.setProperty(property, style.value, style.priority);
      } else {
        element.style.removeProperty(property);
      }
    });
  };

  const createScrollLock = () => {
    const locks = new Set();
    const rootProperties = ['overflow', 'scroll-behavior'];
    const bodyProperties = [
      'overflow',
      'position',
      'top',
      'left',
      'right',
      'width',
      'padding-right',
      'scroll-behavior',
    ];
    let savedState = null;

    const lock = (token, options = {}) => {
      const lockToken = token || document.body;
      if (locks.has(lockToken)) return;

      locks.add(lockToken);
      if (locks.size > 1) return;

      const root = document.documentElement;
      const body = document.body;
      const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
      const bodyPaddingRight = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;

      savedState = {
        scrollX: window.scrollX || 0,
        scrollY: window.scrollY || root.scrollTop || 0,
        preserveLayout: Boolean(options.preserveLayout),
        rootStyles: captureStyles(root, rootProperties),
        bodyStyles: captureStyles(body, bodyProperties),
      };

      root.style.setProperty('overflow', 'hidden');
      body.style.setProperty('overflow', 'hidden');

      if (!savedState.preserveLayout) {
        body.style.setProperty('position', 'fixed');
        body.style.setProperty('top', `-${savedState.scrollY}px`);
        body.style.setProperty('left', `-${savedState.scrollX}px`);
        body.style.setProperty('right', '0');
        body.style.setProperty('width', '100%');

        if (scrollbarWidth > 0) {
          body.style.setProperty('padding-right', `${bodyPaddingRight + scrollbarWidth}px`);
        }
      }
    };

    const unlock = (token) => {
      const lockToken = token || document.body;
      if (!locks.has(lockToken)) return;

      locks.delete(lockToken);
      if (locks.size > 0 || !savedState) return;

      const root = document.documentElement;
      const body = document.body;
      const state = savedState;
      savedState = null;

      root.style.setProperty('scroll-behavior', 'auto', 'important');
      body.style.setProperty('scroll-behavior', 'auto', 'important');
      restoreStyles(root, state.rootStyles, ['scroll-behavior']);
      restoreStyles(body, state.bodyStyles, ['scroll-behavior']);
      window.scrollTo(state.scrollX, state.scrollY);
      restoreStyles(root, { 'scroll-behavior': state.rootStyles['scroll-behavior'] });
      restoreStyles(body, { 'scroll-behavior': state.bodyStyles['scroll-behavior'] });
    };

    return { lock, unlock };
  };

  const mirrorConceptHeaderState = () => {
    const headerSection = document.querySelector('.header-section');
    if (!headerSection) return;

    headerSection.classList.add('section-header');
    if (headerSection.querySelector('header[is="sticky-header"]')) {
      headerSection.classList.add('shopify-section-header-sticky');
    }

    const sync = () => {
      const root = document.documentElement;
      headerSection.classList.toggle('shopify-section-header-hidden', root.classList.contains('header-hidden'));
      headerSection.classList.toggle(
        'menu-open',
        Boolean(document.querySelector('.header-group [open], .header-group [aria-expanded="true"]'))
      );
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    observer.observe(headerSection, { attributes: true, subtree: true, attributeFilter: ['open', 'aria-expanded'] });
  };

  const adaptAspadzProductInfo = (scope = document) => {
    scope.querySelectorAll?.('product-info[data-aspadz-product-info-root]').forEach((productInfo) => {
      if (productInfo.dataset.aspadzConceptAdapted === 'true') return;

      productInfo.dataset.aspadzConceptAdapted = 'true';
      productInfo.buildRequestUrlWithParams = (url, optionValues) => {
        const requestUrl = new URL(url, window.location.origin);
        const currentUrl = new URL(window.location.href);
        const view = currentUrl.searchParams.get('view');

        if (view) requestUrl.searchParams.set('view', view);
        requestUrl.searchParams.set('section_id', productInfo.sectionId);
        if (optionValues.length) requestUrl.searchParams.set('option_values', optionValues.join(','));

        return `${requestUrl.pathname}${requestUrl.search}`;
      };
    });
  };

  const adaptConceptCartDrawer = (scope = document) => {
    scope.querySelectorAll?.('cart-drawer').forEach((drawer) => {
      if (drawer.dataset.aspadzConceptAdapted === 'true' || typeof drawer.show !== 'function') return;

      drawer.dataset.aspadzConceptAdapted = 'true';

      const show = drawer.show.bind(drawer);
      drawer.show = (...args) => {
        document.documentElement.classList.add('aspadz-cart-drawer-open');
        document.dispatchEvent(new CustomEvent('aspadz:cart-drawer:opening'));
        return show(...args);
      };

      if (typeof drawer.hide === 'function') {
        const hide = drawer.hide.bind(drawer);
        drawer.hide = (...args) => {
          document.documentElement.classList.remove('aspadz-cart-drawer-open');
          return hide(...args);
        };
      }

      // ModalElement toggles the drawer's `open` attribute but never touches the
      // triggers, whose aria-expanded is hardcoded to "false" in Liquid.
      const syncTriggerExpandedState = () => {
        const expanded = String(drawer.hasAttribute('open'));

        Array.from(document.querySelectorAll(`[aria-controls="${drawer.id}"]`))
          .filter((control) => !drawer.contains(control))
          .forEach((control) => {
            if (control.getAttribute('aria-expanded') === expanded) return;

            control.setAttribute('aria-expanded', expanded);
          });
      };

      new MutationObserver(syncTriggerExpandedState).observe(drawer, {
        attributeFilter: ['open'],
      });
      syncTriggerExpandedState();

      // A link only activates on Enter, so the SPACE key live supports needs
      // handling here. Restricted to links outside the drawer: buttons already
      // fire a click on SPACE, and doing both would toggle twice.
      document.addEventListener('keydown', (event) => {
        if (event.code !== 'Space' && event.key !== ' ') return;

        const control = event.target.closest?.(`a[aria-controls="${drawer.id}"]`);

        if (!control || drawer.contains(control)) return;

        event.preventDefault();
        drawer.hasAttribute('open') ? drawer.hide() : drawer.show(control);
      });
    });
  };

  // CartCount writes a bare number into <cart-count>; the `item`/`items` word
  // next to it is static Liquid, so it needs syncing whenever the count moves
  // across the singular/plural boundary.
  const syncCartDrawerCountLabel = (itemCount) => {
    const count = Number.parseInt(itemCount, 10);

    if (!Number.isFinite(count)) return;

    const label = count === 1 ? 'item' : 'items';

    document.querySelectorAll('[data-aspadz-cart-count-label]').forEach((element) => {
      if (element.textContent.trim() === label) return;

      element.textContent = label;
    });
  };

  let cartCountLabelSubscribed = false;

  const observeCartCountLabel = () => {
    if (cartCountLabelSubscribed || typeof themeRuntime?.pubsub?.subscribe !== 'function') return;

    cartCountLabelSubscribed = true;

    themeRuntime.pubsub.subscribe(themeRuntime.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event?.cart || event.cart.errors) return;

      syncCartDrawerCountLabel(event.cart.item_count);
    });
  };

  // The Cart API reports a rejected quantity change as either a plain string or
  // an object keyed by line, and cart.js hands it straight to
  // setCustomValidity(), which would stringify the object.
  const cartErrorMessage = (errors, lineKey) => {
    if (typeof errors === 'string') return errors;

    if (!errors || typeof errors !== 'object') return '';

    if (errors[lineKey]) return String(errors[lineKey]);

    const messages = Object.values(errors).filter(Boolean);

    return messages.length === 1 ? String(messages[0]) : messages.map(String).join(' ');
  };

  const updateCartDrawerLineError = (event) => {
    const lineKey = event.target?.getAttribute?.('data-index');
    const container = lineKey ? document.getElementById(`CartDrawer-LineItemError-${lineKey}`) : null;
    const text = container?.querySelector('.cart-item__error-text');

    if (!text) return;

    const message = cartErrorMessage(event.cart.errors, lineKey);

    if (text.textContent !== message) text.textContent = message;

    // cart.js leaves the input invalid after reporting, which is what makes the
    // native bubble appear; clearing it leaves the inline message as the signal.
    if (typeof event.target.setCustomValidity === 'function') event.target.setCustomValidity('');
  };

  let cartLineErrorSubscribed = false;

  const observeCartLineErrors = () => {
    if (cartLineErrorSubscribed || typeof themeRuntime?.pubsub?.subscribe !== 'function') return;

    cartLineErrorSubscribed = true;

    themeRuntime.pubsub.subscribe(themeRuntime.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event?.cart?.errors) return;

      updateCartDrawerLineError(event);
    });
  };

  // The recommendation card's artwork and title are links to the product page.
  // The add control next to them is a submit button, so it is not inside a link
  // and its click cannot navigate — this guard only cancels navigation if a link
  // action is ever reintroduced. It is delegated from the document because the
  // drawer re-renders its contents after every cart update.
  let cartRecommendationActionGuarded = false;

  const guardCartRecommendationActions = () => {
    if (cartRecommendationActionGuarded) return;

    cartRecommendationActionGuarded = true;

    document.addEventListener('click', (event) => {
      const action = event.target.closest?.('.aspadz-cart-recommendations__action');

      // A submit button keeps its default action, which submits its product form.
      if (!action || action.tagName !== 'A') return;

      event.preventDefault();
    });
  };

  const adaptAspadzLocalization = (scope = document) => {
    scope.querySelectorAll?.('[data-aspadz-localization-form]').forEach((wrapper) => {
      if (wrapper.dataset.aspadzConceptAdapted === 'true') return;

      wrapper.dataset.aspadzConceptAdapted = 'true';
      const form = wrapper.querySelector('form');
      const trigger = wrapper.querySelector('.localization-form__select');
      const disclosure = wrapper.querySelector('.disclosure__list-wrapper');
      const filter = wrapper.querySelector('.country-filter__input');
      const resetButton = wrapper.querySelector('.country-filter__reset-button');

      const setOpen = (open) => {
        if (open) {
          // Close sibling disclosures in the same container for mutual exclusivity
          const container = wrapper.closest('.aspadz-header__localization-forms') || wrapper.parentElement;
          container?.querySelectorAll('[data-aspadz-localization-form]').forEach((otherWrapper) => {
            if (otherWrapper !== wrapper) {
              otherWrapper.querySelector('.localization-form__select')?.setAttribute('aria-expanded', 'false');
              otherWrapper.querySelector('.disclosure__list-wrapper')?.setAttribute('hidden', '');
            }
          });
        }
        trigger?.setAttribute('aria-expanded', String(open));
        disclosure?.toggleAttribute('hidden', !open);
        if (open && filter) {
          window.requestAnimationFrame(() => filter.focus());
        }
      };

      trigger?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(disclosure?.hasAttribute('hidden'));
      });

      wrapper.querySelector('.country-selector__close-button')?.addEventListener('click', () => setOpen(false));

      wrapper.querySelectorAll('.disclosure__link[data-value]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const input = form?.querySelector('input[name="country_code"], input[name="locale_code"]');
          if (!input || !form) return;
          input.value = link.dataset.value;
          form.submit();
        });
      });

      if (filter) {
        const handleFilter = () => {
          const query = filter.value.trim().toLocaleLowerCase();
          resetButton?.classList.toggle('hidden', !query);
          wrapper.querySelectorAll('.countries .disclosure__item').forEach((item) => {
            item.toggleAttribute('hidden', Boolean(query) && !item.textContent.toLocaleLowerCase().includes(query));
          });
          const popularList = wrapper.querySelector('.popular-countries');
          if (popularList) {
            popularList.toggleAttribute('hidden', Boolean(query));
          }
        };

        filter.addEventListener('input', handleFilter);

        resetButton?.addEventListener('click', (e) => {
          e.preventDefault();
          filter.value = '';
          handleFilter();
          filter.focus();
        });
      }

      document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target)) {
          setOpen(false);
        }
      });

      disclosure?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          setOpen(false);
          trigger?.focus();
        }
      });
    });
  };

  const getConceptCartSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
    );
    return Array.from(new Set(sections));
  };

  const refreshConceptCart = async ({ source, productVariantId, sections, open = false }) => {
    const cartState = await (
      await fetch(themeRuntime.routes.cart_url, themeRuntime.utils.fetchConfig('json', 'GET'))
    ).json();
    cartState.sections = sections || {};

    themeRuntime.pubsub.publish(themeRuntime.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source,
      productVariantId,
      cart: cartState,
    });
    if (open) document.querySelector('cart-drawer')?.show?.();
    return cartState;
  };

  // -- Carousel adapter
  class AspadzConceptCarousel {
    constructor(element, options = {}) {
      if (!element) throw new Error('AspadzConceptCarousel requires a slider element.');
      if (typeof window.Flickity !== 'function') throw new Error('Concept Flickity runtime is unavailable.');

      this.element = element;
      this.track = element.querySelector('.swiper-wrapper') || element;
      this.params = { ...options };
      this.originalParams = { ...options };
      this.events = options.on || {};
      this.cellSelector = options.cellSelector || '.swiper-slide';
      this.slides = Array.from(this.track.querySelectorAll(this.cellSelector));
      this.activeIndex = Number(options.initialSlide) || 0;
      this.realIndex = this.activeIndex;
      this.destroyed = false;
      this.navigationHandlers = [];
      this.resizeHandler = this.onResize.bind(this);
      this.track.classList.add('aspadz-concept-carousel');
      this.track.classList.toggle('aspadz-concept-carousel--fade', options.effect === 'fade');

      this.applyResponsiveLayout();
      this.buildPagination();
      this.bindNavigation();

      const autoplay = this.resolveAutoplay();
      this.instance = new window.Flickity(this.track, {
        autoPlay: autoplay,
        cellAlign: options.centeredSlides ? 'center' : 'left',
        cellSelector: this.cellSelector,
        contain: !options.centeredSlides && !options.loop,
        draggable: options.allowTouchMove !== false,
        initialIndex: this.activeIndex,
        pageDots: false,
        pauseAutoPlayOnHover: options.autoplay?.pauseOnMouseEnter !== false,
        prevNextButtons: false,
        rightToLeft: document.documentElement.dir === 'rtl',
        selectedAttraction: 0.025,
        friction: 0.3,
        wrapAround: Boolean(options.loop || options.rewind),
        on: {
          ready: () => window.queueMicrotask(() => this.onReady()),
          select: () => this.onSelect(),
          settle: () => this.events.slideChangeTransitionEnd?.(this),
        },
      });

      this.autoplay = {
        start: () => this.instance?.playPlayer?.(),
        stop: () => this.instance?.stopPlayer?.(),
        pause: () => this.instance?.pausePlayer?.(),
        resume: () => {
          this.instance?.unpausePlayer?.();
          this.events.autoplayResume?.(this);
        },
      };

      this.element.swiper = this;
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }

    resolveOptions() {
      const resolved = { ...this.originalParams };
      const viewportWidth = document.documentElement.clientWidth;

      Object.entries(this.originalParams.breakpoints || {})
        .map(([width, values]) => [Number(width), values])
        .filter(([width]) => viewportWidth >= width)
        .sort(([first], [second]) => first - second)
        .forEach(([, values]) => Object.assign(resolved, values));

      return resolved;
    }

    resolveAutoplay() {
      const autoplay = this.params.autoplay;
      if (!autoplay || themeRuntime?.config?.motionReduced) return false;
      if (autoplay === true) return 5000;
      return Number(autoplay.delay) || 5000;
    }

    applyResponsiveLayout() {
      const resolved = this.resolveOptions();
      const gap = Number(resolved.spaceBetween) || 0;
      const offsetValue = typeof resolved.slidesOffsetBefore === 'function'
        ? resolved.slidesOffsetBefore()
        : resolved.slidesOffsetBefore;
      const offset = Number(offsetValue) || 0;
      const slidesPerView = Number(resolved.slidesPerView);
      const availableWidth = Math.max(this.element.clientWidth - offset, 0);
      const slideWidth = Number.isFinite(slidesPerView) && slidesPerView > 0
        ? Math.max((availableWidth - gap * Math.max(slidesPerView - 1, 0)) / slidesPerView, 0)
        : null;

      this.slides.forEach((slide, index) => {
        if (slideWidth == null) {
          slide.style.removeProperty('width');
        } else {
          slide.style.width = `${slideWidth}px`;
        }
        slide.style.marginInlineEnd = index === this.slides.length - 1 ? '0px' : `${gap}px`;
      });
      this.track.style.paddingInlineStart = offset ? `${offset}px` : '';
    }

    buildPagination() {
      const pagination = this.originalParams.pagination;
      if (!pagination?.el) return;

      this.paginationElement = pagination.el;
      const renderBullet = pagination.renderBullet || ((index, className) => `<button class="${className}" type="button" aria-label="Go to slide ${index + 1}"></button>`);
      this.paginationElement.innerHTML = this.slides
        .map((_, index) => renderBullet(index, 'swiper-pagination-bullet'))
        .join('');
      this.paginationBullets = Array.from(this.paginationElement.querySelectorAll('.swiper-pagination-bullet'));

      if (pagination.clickable) {
        this.paginationBullets.forEach((bullet, index) => {
          bullet.addEventListener('click', () => this.slideTo(index));
        });
      }
    }

    bindNavigation() {
      const navigation = this.originalParams.navigation;
      if (!navigation) return;

      const bindings = [
        [navigation.prevEl, () => this.slidePrev()],
        [navigation.nextEl, () => this.slideNext()],
      ];
      bindings.forEach(([button, handler]) => {
        if (!button) return;
        button.addEventListener('click', handler);
        this.navigationHandlers.push([button, handler]);
      });
    }

    onReady() {
      if (this.destroyed) return;
      this.updateState();
      this.events.init?.(this);
    }

    onSelect() {
      if (this.destroyed) return;
      this.updateState();
      this.events.slideChangeTransitionStart?.(this);
      this.events.slideChange?.(this);
    }

    updateState() {
      this.activeIndex = this.instance?.selectedIndex ?? this.activeIndex;
      this.realIndex = this.activeIndex;
      this.isBeginning = this.activeIndex === 0;
      this.isEnd = this.activeIndex === Math.max(this.slides.length - 1, 0);

      this.slides.forEach((slide, index) => {
        slide.classList.toggle('swiper-slide-active', index === this.activeIndex);
      });
      this.paginationBullets?.forEach((bullet, index) => {
        const active = index === this.activeIndex;
        bullet.classList.toggle('swiper-pagination-bullet-active', active);
        bullet.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const navigation = this.originalParams.navigation;
      if (navigation && !this.originalParams.loop && !this.originalParams.rewind) {
        navigation.prevEl?.classList.toggle('swiper-button-disabled', this.isBeginning);
        navigation.prevEl?.toggleAttribute('disabled', this.isBeginning);
        navigation.nextEl?.classList.toggle('swiper-button-disabled', this.isEnd);
        navigation.nextEl?.toggleAttribute('disabled', this.isEnd);
      }
    }

    onResize() {
      if (this.destroyed) return;
      this.applyResponsiveLayout();
      this.instance?.resize?.();
      this.events.resize?.(this);
    }

    slideTo(index, speed = undefined, runCallbacks = true) {
      if (this.destroyed) return;
      this.instance?.select?.(Number(index) || 0, Boolean(this.originalParams.loop), speed === 0);
      if (!runCallbacks) this.updateState();
    }

    slideToLoop(index, speed = undefined, runCallbacks = true) {
      this.slideTo(index, speed, runCallbacks);
    }

    slideNext() {
      this.instance?.next?.(Boolean(this.originalParams.loop || this.originalParams.rewind));
    }

    slidePrev() {
      this.instance?.previous?.(Boolean(this.originalParams.loop || this.originalParams.rewind));
    }

    update() {
      this.applyResponsiveLayout();
      this.instance?.reloadCells?.();
      this.instance?.resize?.();
      this.updateState();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      window.removeEventListener('resize', this.resizeHandler);
      this.navigationHandlers.forEach(([button, handler]) => button.removeEventListener('click', handler));
      this.navigationHandlers = [];
      this.instance?.destroy?.();
      this.track.classList.remove('aspadz-concept-carousel', 'aspadz-concept-carousel--fade');
      this.track.style.removeProperty('padding-inline-start');
      this.slides.forEach((slide) => {
        slide.style.removeProperty('width');
        slide.style.removeProperty('margin-inline-end');
        slide.classList.remove('swiper-slide-active');
      });
      if (this.element.swiper === this) delete this.element.swiper;
    }
  }

  // -- Lifecycle hooks
  exposeConceptRuntime();
  window.AspadzScrollLock = window.AspadzScrollLock || createScrollLock();
  window.AspadzConceptCarousel = AspadzConceptCarousel;
  window.AspadzConceptCart = {
    getSections: getConceptCartSections,
    refresh: refreshConceptCart,
  };

  if (!customElements.get('header-drawer')) {
    customElements.define(
      'header-drawer',
      class AspadzHeaderDrawer extends HTMLElement {
        get mainDetailsToggle() {
          return this.querySelector(':scope > details');
        }

        connectedCallback() {
          this.details = this.mainDetailsToggle;
          this.summary = this.details?.querySelector(':scope > summary');
          if (!this.details || !this.summary) return;

          this.onToggle = () => this.syncDrawerState();
          this.onKeyUp = (event) => {
            if (event.code.toUpperCase() !== 'ESCAPE' || !this.details.open) return;

            event.preventDefault();
            this.closeMenuDrawer(event, this.summary);
          };

          this.summary.setAttribute('aria-expanded', String(this.details.open));
          this.details.addEventListener('toggle', this.onToggle);
          this.addEventListener('keyup', this.onKeyUp);
          this.syncDrawerState();
        }

        disconnectedCallback() {
          this.details?.removeEventListener('toggle', this.onToggle);
          this.removeEventListener('keyup', this.onKeyUp);
          this.teardownOpenState();
        }

        syncDrawerState() {
          if (!this.details || !this.summary) return;

          const open = this.details.open;
          this.summary.setAttribute('aria-expanded', String(open));
          document.documentElement.classList.toggle('aspadz-header-drawer-open', open);

          if (!open) {
            this.teardownOpenState();
            return;
          }

          const header = this.closest('.shopify-section-header, .header-section');
          const headerBottom = header?.getBoundingClientRect().bottom;
          if (Number.isFinite(headerBottom)) {
            document.documentElement.style.setProperty('--header-bottom-position', `${Math.round(headerBottom)}px`);
          }

          document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint || 'mobile'}`);
          window.AspadzScrollLock?.lock(this, { preserveLayout: true });

          window.requestAnimationFrame(() => {
            if (!this.details?.open) return;

            this.details.classList.add('menu-opening');
            window.trapFocus?.(this.details, this.summary);
          });
        }

        teardownOpenState(focusTarget) {
          if (!this.details) return;

          this.details.classList.remove('menu-opening');
          this.details.querySelectorAll(':scope details[open]').forEach((details) => {
            details.removeAttribute('open');
            details.classList.remove('menu-opening');
            details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
          });
          this.details.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
          this.summary?.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('aspadz-header-drawer-open');
          document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint || 'mobile'}`);
          window.AspadzScrollLock?.unlock(this);
          window.removeTrapFocus?.(focusTarget);
        }

        closeMenuDrawer(_event, focusTarget) {
          if (!this.details) return;

          this.teardownOpenState(focusTarget);
          this.details.removeAttribute('open');
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        mirrorConceptHeaderState();
        adaptAspadzProductInfo();
        adaptConceptCartDrawer();
        adaptAspadzLocalization();
        observeCartCountLabel();
        observeCartLineErrors();
        guardCartRecommendationActions();
      },
      { once: true }
    );
  } else {
    mirrorConceptHeaderState();
    adaptAspadzProductInfo();
    adaptConceptCartDrawer();
    adaptAspadzLocalization();
    observeCartCountLabel();
    observeCartLineErrors();
    guardCartRecommendationActions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    adaptAspadzProductInfo(event.target);
    adaptConceptCartDrawer(event.target);
    adaptAspadzLocalization(event.target);
  });
})();
